import { format, parseISO } from "date-fns"
import utcToZonedTime from "date-fns-tz/utcToZonedTime"
import React from "react"

import Badge from "src/components/Badge"
import BoatPreviewLink from "src/components/BoatPreviewLink"
import CellSelect from "src/components/DataTableV2/CellSelect"
import TextCollapsible from "src/components/TextCollapsible"
import Tooltip from "src/components/Tooltip"

import { titlecase } from "src/utils/string_helpers"

import { timeCellContent } from "./MarinaScheduleSharedMethods"

const BOATER_STATUS_OPTIONS = {
  scheduled: [{ value: "arrived", label: "Boater arrived", color: "blue" }],
  launched: [
    { value: "ready_for_return", label: "Ready for return", color: "teal" },
    {
      value: "not_ready_for_return",
      label: "Do not return",
      color: "yellow",
    },
  ],
}

const getBoaterStatusLabel = (status, boaterStatus) => {
  const options = BOATER_STATUS_OPTIONS[status] || []
  const option = options.find((opt) => opt.value === boaterStatus)
  if (!option) return null
  return <Badge text={option.label} color={option.color} variant="small" />
}

const contactColumn = ({ marinaSlug }) => ({
  key: "contact",
  header: "Customer",
  cardRole: "title",
  render: (row) => {
    const boat = row.contactBoat

    return (
      <div className="overflow-hidden">
        <div className="truncate">{row.contact.name}</div>
        <div className="text-muted truncate text-xs">
          <BoatPreviewLink boat={boat} />
        </div>
      </div>
    )
  },
  cardRender: (row) => {
    const boat = row.contactBoat

    return <BoatPreviewLink boat={boat} />
  },
  width: "180px",
})

const balanceColumn = () => ({
  key: "balance",
  header: "",
  cardRole: "subtitle",
  render: (row) =>
    row.contact?.hasOutstandingBalance ? (
      <Tooltip text="This boater has an outstanding balance" variant="light">
        <i className="icon icon-warning-outline text-lg text-yellow-700" />
      </Tooltip>
    ) : null,
  cardRender: (row) => (
    <div className="flex items-center gap-2">
      <span>{row.contact.name}</span>
      {row.contact?.hasOutstandingBalance && (
        <Tooltip text="This boater has an outstanding balance" variant="light">
          <i className="icon icon-warning-outline text-yellow-700" />
        </Tooltip>
      )}
    </div>
  ),
  width: "20px",
})

const spaceNameColumn = () => ({
  key: "spaceName",
  header: "From",
  cardRole: "detail",
  render: (row) => row.spaceName || "-",
  width: "150px",
})

const locationColumn = () => ({
  key: "location",
  header: "To",
  cardRole: "detail",
  render: (row) => (row.location ? titlecase(row.location) : "-"),
  width: "150px",
})

const boaterStatusColumn = ({ onUpdateBoaterStatus, canManage }) => ({
  key: "boaterStatus",
  header: "Boater Status",
  cardRole: "badge",
  render: (row) => {
    const options = BOATER_STATUS_OPTIONS[row.status] || []
    return (
      <CellSelect
        value={row.boaterStatus}
        label={getBoaterStatusLabel(row.status, row.boaterStatus)}
        placeholder="Set status"
        onChange={(value) => onUpdateBoaterStatus(row.encodedId, value)}
        disabled={!canManage}
        clearLabel="Clear status"
      >
        {options.map((option) => (
          <CellSelect.Option key={option.value} value={option.value}>
            <Badge text={option.label} color={option.color} variant="small" />
          </CellSelect.Option>
        ))}
      </CellSelect>
    )
  },
})

const estimatedReturnColumn = () => ({
  key: "estimatedReturnTime",
  header: "Est. Return",
  cardRole: "detail",
  render: (row) => {
    if (!row.estimatedReturnTime) return "-"
    const zonedTime = utcToZonedTime(
      parseISO(row.estimatedReturnTime),
      row.marinaTimezone
    )
    return (
      <div>
        <div>{format(parseISO(row.estimatedReturnTime), "MM/dd/yy")}</div>
        <div className="text-muted text-xs">{format(zonedTime, "p")}</div>
      </div>
    )
  },
  cardRender: (row) => {
    if (!row.estimatedReturnTime) return null
    const zonedTime = utcToZonedTime(
      parseISO(row.estimatedReturnTime),
      row.marinaTimezone
    )
    return (
      <span className="text-muted">
        Est. Return: {format(zonedTime, "MMMM dd, yyyy 'at' p")}
      </span>
    )
  },
})

const launchNoteColumn = () => ({
  key: "launchNote",
  header: "Launch Note",
  cardRole: "footer",
  render: (row) => {
    if (!row.note) return "-"
    return (
      <TextCollapsible lines={2} className="py-2">
        <span>{row.note}</span>
      </TextCollapsible>
    )
  },
  cardRender: (row) => {
    if (!row.note) return null
    return (
      <TextCollapsible lines={2}>
        <p className="mb-0 font-semibold">Launch Note:</p>
        <span>{row.note}</span>
      </TextCollapsible>
    )
  },
  width: "300px",
})

const reservationNoteColumn = () => ({
  key: "reservationNote",
  header: "Contract/Res. Note",
  cardRole: "footer",
  render: (row) => {
    if (!row.reservationNote) return "-"
    return (
      <TextCollapsible lines={2} className="py-2">
        <span>{row.reservationNote}</span>
      </TextCollapsible>
    )
  },
  cardRender: (row) => {
    if (!row.reservationNote) return null
    const label = row.belongsToContract ? "Contract Note:" : "Reservation Note:"
    return (
      <TextCollapsible lines={2}>
        <p className="mb-0 font-semibold">{label}</p>
        <span>{row.reservationNote}</span>
      </TextCollapsible>
    )
  },
  width: "300px",
})

export const getScheduledColumns = ({
  viewingAll,
  unviewedIds,
  onUpdateBoaterStatus,
  canManage,
  marinaSlug,
}) => [
  {
    key: "unviewed",
    header: "",
    cardRole: "hidden",
    render: (row) =>
      unviewedIds.has(row.encodedId) ? (
        <div className="h-2 w-2 rounded-full bg-blue-600" />
      ) : (
        <div />
      ),
    width: "20px",
  },
  {
    key: "startTime",
    header: "Scheduled",
    cardRole: "title",
    render: (row) => timeCellContent(row, viewingAll),
    cardRender: (row) => (
      <div>
        {timeCellContent(row, viewingAll)}
        <div className="my-4 w-full border-b" />
      </div>
    ),
    width: "225px",
  },
  contactColumn({ marinaSlug }),
  balanceColumn(),
  spaceNameColumn(),
  locationColumn(),
  boaterStatusColumn({ onUpdateBoaterStatus, canManage }),
  estimatedReturnColumn(),
  launchNoteColumn(),
  reservationNoteColumn(),
]

export const getLaunchedColumns = ({
  onUpdateBoaterStatus,
  canManage,
  marinaSlug,
}) => [
  {
    key: "startTime",
    header: "Launched",
    cardRole: "title",
    render: (row) => timeCellContent(row, false),
    cardRender: (row) => (
      <div>
        {timeCellContent(row, false)}
        <div className="my-4 w-full border-b" />
      </div>
    ),
  },
  contactColumn({ marinaSlug }),
  balanceColumn(),
  spaceNameColumn(),
  locationColumn(),
  boaterStatusColumn({ onUpdateBoaterStatus, canManage }),
  estimatedReturnColumn(),
  launchNoteColumn(),
  reservationNoteColumn(),
]

export const getReturnedColumns = ({ marinaSlug }) => [
  {
    key: "startTime",
    header: "Returned",
    cardRole: "title",
    render: (row) => timeCellContent(row, false),
    cardRender: (row) => (
      <div>
        {timeCellContent(row, false)}
        <div className="my-4 w-full border-b" />
      </div>
    ),
  },
  contactColumn({ marinaSlug }),
  balanceColumn(),
  spaceNameColumn(),
  locationColumn(),
  launchNoteColumn(),
  reservationNoteColumn(),
]
